import type { ReactNode } from "react";

// Text that a person typed (a name, a label, a file name) is page text only, never an attribute: error reports (Sentry) copy the attributes of
// the elements around an error. A control whose accessible name has such text in it points at the page text instead, with `aria-labelledby`:
//
//   <span id="person-name-0">Ana Ruiz</span> ...
//   <Button aria-labelledby="person-remove-0 person-name-0">Remove</Button>   // the name is "Remove Ana Ruiz"
//
// The fixed words of the name ("Move", "up", "Remove category") sit in `NameWord` elements. They are `hidden`, which takes them out of
// the page and of the reading order; a name that points at them directly still uses their text.

/** A fixed word that belongs to an accessible name built with `aria-labelledby`. */
export function NameWord({ id, children }: Readonly<{ id: string; children: ReactNode }>) {
  return (
    <span hidden id={id}>
      {children}
    </span>
  );
}

/**
 * A short key made from text, for a place where an element needs a stable key or id for a row but the text itself must not be in the
 * attribute (a slug, a storage path with a file name). The same text always gives the same key; the text cannot be read back from it.
 */
export function opaqueKey(text: string): string {
  let hash = 0x811c9dc5;
  for (let index = 0; index < text.length; index += 1) {
    hash ^= text.charCodeAt(index);
    hash = Math.imul(hash, 0x01000193);
  }
  return `k${(hash >>> 0).toString(16)}`;
}
